import './select2-more-less-links.css';

import h from 'hyperscript';

import { throttle } from 'cadenza/utils/throttle';

import i18n from './messages.properties';

const MAX_DISPLAYED_LINE_COUNT = 3;
const THROTTLE_DELAY = 100;
/**
 * If more then MAX_DISPLAYED_LINE_COUNT options chosen
 * offer button link for showing more/less options.
 *
 * @param {HTMLElement} select - The plain HTML select element
 * @return {Function} function which destroys more less util
 */
export function initializeMoreLess (select) {
  if (!select.classList.contains('is-expanded')) {
    select.classList.toggle('is-expanded', false);
  }

  initializeMoreLessButton(select);
  const select2ChoicesElement = select.parentElement.querySelector('.select2-selection__rendered');
  select2ChoicesElement.classList.add('with-more-less');
  if (select2ChoicesElement) {
    // ResizeObserver: line count must be recalculated when resizing select element
    const update = throttle(_update, THROTTLE_DELAY);
    const resizeObserver = new ResizeObserver(() => update(select));
    resizeObserver.observe(select.parentElement);

    _update(select);

    return {
      destroy: () => {
        resizeObserver.disconnect();
        select.parentElement.querySelector('.d-select--more-less').remove();
      },
      update: () => { _update(select); }
    };
  }

  return () => {};
}

function initializeMoreLessButton (select) {
  const moreLessButton = h('button.d-anchor.d-select--more-less');
  moreLessButton.addEventListener('click', (event) => {
    toggle(select, event);
  });
  select.nextElementSibling.append(moreLessButton);
  return select;
}

function _update (select) {
  if (!select.parentElement) {
    return; // this is throttled and in IE the occurs sometimes after drag and drop, at this time the parentElement is undefined
  }
  const focused = select.parentElement.querySelector('.select2-container--focus');

  const numberOfHiddenItems = rerenderChoices(select);

  if (focused) {
    focusSearchField(select);
  }
  const moreLessButton = select.parentElement.querySelector('.d-select--more-less');
  if (moreLessButton) {
    if (numberOfHiddenItems === 0) {
      moreLessButton.hidden = true;
      return true;
    }

    moreLessButton.textContent = select.classList.contains('is-expanded')
      ? i18n('less')
      : i18n('more', { 'count': numberOfHiddenItems });
    moreLessButton.hidden = false;
  }
}

/*
 * More less functionality causes focus loss on search field.
 * This function resets the focus.
 */
function focusSearchField (select) {
  setTimeout(() => {
    select.parentElement.searchInput.focus();
  });
}

function toggle (select, event) {
  select.classList.toggle('is-expanded', !select.classList.contains('is-expanded'));
  select.dispatchEvent(new CustomEvent('more-less-toggle'));
  _update(select);
}

/**
 * Renders selected options
 *
 * @param {HTMLElement} select - The plain HTML select element
 * @return {number} number of items that were hidden
 */
function rerenderChoices (select) {
  const select2Container = select.nextElementSibling;
  let hiddenItemsCount = 0;

  if (!select2Container) {
    return hiddenItemsCount;
  }
  const choices = [ ...select2Container.querySelectorAll('.select2-selection__choice') ];

  if (!choices.length) {
    return hiddenItemsCount;
  }

  const isExtended = select.classList.contains('is-expanded');

  let currentLineTop = -1;
  let currentLineCount = 0;
  choices.forEach(choice => {
    choice.classList.toggle('visuallyhidden', false);
    const choiceTop = choice.offsetTop;

    if (choiceTop > currentLineTop) {
      currentLineTop = choiceTop;
      currentLineCount++;
    }

    const hidden = (currentLineCount > MAX_DISPLAYED_LINE_COUNT);
    // if is-expanded than remove visuallyhidden (could be set) but count options
    choice.classList.toggle('visuallyhidden', !isExtended && hidden);
    if (hidden) {
      hiddenItemsCount++;
    }
  });

  return hiddenItemsCount;
}
